import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import { requirementOf } from "../feedback/requirement";
import AnchoredTooltip from "../overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../overlays/useAnchoredTooltip";
import { classNames } from "../styles/classNames";
import { switcherOptionClassName, type SwitcherSize } from "../styles/switcherStyles";

export interface SegmentOption<K extends string> {
  readonly key: K;
  readonly label: ReactNode;
  /** A longer explanation, shown on hover or focus in the shared tooltip (IM-RULES). */
  readonly title?: string;
  readonly disabled?: boolean;
  /** The served decision choosing it needs (SOT-02, accounts.md GS-8): refused about where things are, it isn't
   * shown; otherwise it is disabled, and its hover says why instead of `title`. */
  readonly requires?: Decision;
}

interface SegmentedControlProps<K extends string> {
  /** Names the group for screen readers: what is being chosen. */
  label: string;
  options: readonly SegmentOption<K>[];
  selected: K;
  onSelect: (key: K) => void;
  size?: SwitcherSize;
  /** Options share the width, as in a narrow sidebar. */
  fill?: boolean;
  className?: string;
}

/**
 * One choice from a few options shown side by side: a filter, a mode or a kind. Each option
 * is a button that says whether it is pressed. For views with their own panel use Tabs.
 */
export default function SegmentedControl<K extends string>({ label, options, selected, onSelect, size = "small", fill = false, className }: SegmentedControlProps<K>) {
  const { t } = useTranslation();
  const tooltip = useAnchoredTooltip<string>();
  const hintId = useId();
  const shown = options.flatMap((option) => {
    const requirement = option.requires === undefined ? null : requirementOf(t, option.requires);
    if (requirement !== null && !requirement.shown) return [];
    const reason = requirement?.reason ?? null;
    // An option one may choose explains itself on hover or focus; a refused one keeps its reason as before.
    return [{ option, reason, hint: reason === null ? (option.title ?? null) : null }];
  });
  return (
    <div className={classNames("flex flex-wrap gap-1", className)} role="group" aria-label={label}>
      {shown.map(({ option, reason, hint }) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={option.key === selected}
          title={reason ?? undefined}
          {...(hint === null ? {} : { "aria-describedby": `${hintId}-${option.key}`, ...tooltip.hoverProps(hint), ...tooltip.focusProps(hint) })}
          disabled={reason !== null || option.disabled}
          className={classNames(switcherOptionClassName(size, option.key === selected), fill && "flex-1")}
          onClick={() => {
            onSelect(option.key);
          }}
        >
          {option.label}
        </button>
      ))}
      {/* Each hint as its option's description, outside the button so it doesn't join the option's name. */}
      {shown.map(({ option, hint }) => (hint === null ? null : <span key={option.key} id={`${hintId}-${option.key}`} className="sr-only">{hint}</span>))}
      {tooltip.shown === null ? null : <AnchoredTooltip anchor={tooltip.shown.anchor}>{tooltip.shown.content}</AnchoredTooltip>}
    </div>
  );
}
